import { useEffect, useRef, useState } from 'react';
import PLATFORM_LOGOS from '../../data/platformLogos';
import { useTranslation } from '../../i18n';

// ── Placeholder icons (swap for real brand SVGs later) ───────────────────────
const ICON_PATHS = [
  'M12 2C7.03 2 3 3.57 3 5.5v13C3 20.43 7.03 22 12 22s9-1.57 9-3.5v-13C21 3.57 16.97 2 12 2zm0 2c4.42 0 7 1.34 7 1.5S16.42 7 12 7 5 5.66 5 5.5 7.58 4 12 4zm7 13.5c0 .16-2.58 1.5-7 1.5s-7-1.34-7-1.5V15.1c1.58.87 4.06 1.4 7 1.4s5.42-.53 7-1.4v2.4zm0-5c0 .16-2.58 1.5-7 1.5s-7-1.34-7-1.5V10.1c1.58.87 4.06 1.4 7 1.4s5.42-.53 7-1.4v2.4z',
  'M19 7h-3V6a4 4 0 00-8 0v1H5a1 1 0 00-1 1v11a3 3 0 003 3h10a3 3 0 003-3V8a1 1 0 00-1-1zm-9-1a2 2 0 014 0v1h-4V6zm8 13a1 1 0 01-1 1H7a1 1 0 01-1-1V9h2v1a1 1 0 002 0V9h4v1a1 1 0 002 0V9h2v10z',
  'M3 3h7v7H3V3zm0 11h7v7H3v-7zm11-11h7v7h-7V3zm0 11h7v7h-7v-7z',
  'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  'M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zM16.2 13h2.8v6h-2.8v-6z',
  'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z',
  'M7 2v11h3v9l7-12h-4l4-8z',
];

function PlatformIcon({
  index,
  className,
  platform,
}: {
  index: number;
  className?: string;
  platform?: string;
}) {
  const logoSrc = platform ? PLATFORM_LOGOS[platform] : undefined;
  if (logoSrc) {
    return (
      <img
        src={logoSrc}
        alt=""
        aria-hidden="true"
        className={className}
        style={{
          objectFit: 'contain',
          filter: 'grayscale(100%)',
          mixBlendMode: 'multiply',
          opacity: 0.8,
        }}
      />
    );
  }
  const i = index % ICON_PATHS.length;
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d={ICON_PATHS[i]} />
    </svg>
  );
}

// ── Column layout ─────────────────────────────────────────────────────────────
type CardType = 'sq' | 'tall';
interface ColConfig {
  slots: CardType[];
  riseDelay: number;
  riseDuration: number;
  collapseDelay: number;
  startFraction: number;
}

const COLS: ColConfig[] = [
  {
    slots: ['tall', 'sq', 'tall'],
    riseDelay: 0,
    riseDuration: 0.48,
    collapseDelay: 0,
    startFraction: 0.82,
  },
  {
    slots: ['tall', 'sq', 'tall'],
    riseDelay: 0.07,
    riseDuration: 0.5,
    collapseDelay: 0.07,
    startFraction: 0.9,
  },
  {
    slots: ['sq', 'tall'],
    riseDelay: 0.15,
    riseDuration: 0.6,
    collapseDelay: 0.15,
    startFraction: 1.0,
  },
  {
    slots: ['sq', 'tall'],
    riseDelay: 0.22,
    riseDuration: 0.65,
    collapseDelay: 0.22,
    startFraction: 1.1,
  },
  {
    slots: ['sq', 'tall'],
    riseDelay: 0.15,
    riseDuration: 0.6,
    collapseDelay: 0.15,
    startFraction: 1.0,
  },
  {
    slots: ['tall', 'sq', 'tall'],
    riseDelay: 0.07,
    riseDuration: 0.5,
    collapseDelay: 0.07,
    startFraction: 0.9,
  },
  {
    slots: ['tall', 'sq', 'tall'],
    riseDelay: 0,
    riseDuration: 0.48,
    collapseDelay: 0,
    startFraction: 0.82,
  },
];

const COL_GAP = 6;
const TOTAL_SLOTS = COLS.reduce((s, c) => s + c.slots.length, 0);
const EXPAND_CARD_H = 460;

const CARD_GRADIENTS = [
  'radial-gradient(circle at 26% 60%, rgba(104, 219, 228, 0.18), transparent 44%), linear-gradient(104deg, #4a86d6 0%, #56a7e7 56%, #66c2ef 100%)',
  'radial-gradient(circle at 22% 58%, rgba(84, 177, 231, 0.16), transparent 42%), linear-gradient(104deg, #4a86d5 0%, #50b0df 52%, #61cfc9 100%)',
  'radial-gradient(circle at 86% 45%, rgba(62, 122, 206, 0.22), transparent 44%), linear-gradient(to left, #4683d2 0%, #53b5e1 55%, #63d4c8 100%)',
];

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v));
}

function buildColPlatforms(platforms: string[]): string[][] {
  const padded = Array.from({ length: TOTAL_SLOTS }, (_, i) => platforms[i % platforms.length]);
  let cursor = 0;
  return COLS.map((col) => {
    const slice = padded.slice(cursor, cursor + col.slots.length);
    cursor += col.slots.length;
    return slice;
  });
}

export default function IntegrationSection() {
  const { t, lang } = useTranslation();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [viewportH, setViewportH] = useState(900);
  const [sqH, setSqH] = useState(196);

  const allPlatforms =
    t.integration.platforms.length > 0 ? t.integration.platforms : t.integration.systems;
  const colPlatforms = buildColPlatforms(allPlatforms);

  const featuredList = t.integration.featured
    .map((name) => allPlatforms.find((p) => p === name))
    .filter((p): p is string => Boolean(p));
  const finalFeatured = featuredList.length === 3 ? featuredList : allPlatforms.slice(0, 3);
  const featuredPlatform = finalFeatured[1] ?? finalFeatured[0] ?? allPlatforms[0] ?? '';
  const featuredIconIndex = Math.max(0, allPlatforms.indexOf(featuredPlatform)) % ICON_PATHS.length;

  const featuredCards = t.integration.featuredCards;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateDimensions = () => {
      setViewportH(window.innerHeight);
      const gridW = Math.min(window.innerWidth, 1280) + 128;
      setSqH(Math.floor((gridW - 6 * COL_GAP) / 7));
    };
    updateDimensions();

    let frameId = 0;
    const update = () => {
      frameId = 0;
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const sectionTop = window.scrollY + rect.top;
      const scrollRange = section.offsetHeight - window.innerHeight;
      if (scrollRange <= 0) {
        setScrollProgress(0);
        return;
      }
      const next = clamp((window.scrollY - sectionTop) / scrollRange, 0, 1);
      setScrollProgress((cur) => (Math.abs(cur - next) < 0.002 ? cur : next));
    };
    const schedule = () => {
      if (!frameId) frameId = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('resize', updateDimensions);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const tlH = sqH * 2 + COL_GAP;

  // ── Scroll phases ──────────────────────────────────────────────────────────
  // Section is 640vh → scroll range ≈ 540vh.
  //
  // Rise:     0    → 0.46   cards emerge from below
  // Collapse: 0.48 → 0.63   outer cols fly off top
  // SqExit:   mid-collapse  center sq cards detach
  // Hold:     0.63 → 0.70   3 tall cards settle
  // Expand:   0.70 → 0.92   cards morph into featured use-case cards
  // Final:    0.92 → 1.0    hold expanded state
  const riseProgress = clamp(scrollProgress / 0.46, 0, 1);
  const collapseProgress = clamp((scrollProgress - 0.48) / 0.15, 0, 1);
  const headerFadeProgress = clamp((scrollProgress - 0.26) / 0.2, 0, 1);
  const expandProgress = clamp((scrollProgress - 0.7) / 0.22, 0, 1);

  const restY = viewportH * 0.22;

  // Sq-exit
  const outerTotalH = 2 * tlH + sqH + 2 * COL_GAP;
  const centerTallTop = restY + sqH + COL_GAP;
  const sqExitTrigger = clamp((outerTotalH - centerTallTop) / (viewportH + 200), 0, 0.85);
  const sqExitProgress = clamp((collapseProgress - sqExitTrigger) / 0.55, 0, 1);

  // During expand, slide center columns upward so tall cards end up centered
  const tallCardCurrentTop = restY + sqH + COL_GAP;
  const tallCardTargetTop = (viewportH - EXPAND_CARD_H) / 2;
  const expandCenterShift = expandProgress * (tallCardTargetTop - tallCardCurrentTop);

  // Fade timings for card content morph
  const integrationContentOpacity = 1 - clamp(expandProgress / 0.4, 0, 1);
  const expandedContentOpacity = clamp((expandProgress - 0.35) / 0.5, 0, 1);

  return (
    <section
      id="integration"
      ref={sectionRef}
      className="relative isolate overflow-clip py-20 lg:h-[640vh] lg:py-0"
      style={{ backgroundColor: '#f0f4f8' }}
    >
      {/* ── Mobile: 3-column grid ───────────────────────────────────────── */}
      <div
        data-static-layout
        className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:hidden"
      >
        <header className="mx-auto max-w-4xl py-16 text-center">
          <h2 className="font-display text-4xl font-semibold tracking-tight text-slate-800 sm:text-5xl">
            {t.integration.title}
          </h2>
          <p className="mt-2 bg-gradient-to-r from-blue-500 via-sky-400 to-teal-400 bg-clip-text text-3xl font-medium tracking-tight text-transparent sm:text-4xl">
            {t.integration.highlight}
          </p>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-slate-500 sm:text-lg">
            {t.integration.description}
          </p>
        </header>

        <div className="flex gap-2 overflow-hidden pb-16">
          <div className="flex flex-1 flex-col gap-2">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="flex aspect-square w-full items-center justify-center rounded-2xl bg-[#e3e9f0]"
              >
                <PlatformIcon
                  index={i}
                  platform={allPlatforms[i]}
                  className="h-8 w-8 text-[#8fa4c0]"
                />
              </div>
            ))}
          </div>
          <div className="flex flex-1 flex-col gap-2" style={{ marginTop: 'calc(50vw / 3)' }}>
            {Array.from({ length: 7 }, (_, i) => {
              const isFeatured = i === 3;
              return (
                <div
                  key={i}
                  className={`flex aspect-square w-full items-center justify-center rounded-2xl ${
                    isFeatured
                      ? 'bg-[radial-gradient(circle_at_30%_30%,rgba(104,219,228,0.4),transparent_60%),linear-gradient(135deg,#4a86d5_0%,#50b0df_52%,#61cfc9_100%)]'
                      : 'bg-[#e3e9f0]'
                  }`}
                >
                  <PlatformIcon
                    index={i + 6}
                    platform={allPlatforms[i + 6]}
                    className={`h-8 w-8 ${isFeatured ? 'text-white' : 'text-[#8fa4c0]'}`}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex flex-1 flex-col gap-2">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className="flex aspect-square w-full items-center justify-center rounded-2xl bg-[#e3e9f0]"
              >
                <PlatformIcon
                  index={i + 13}
                  platform={allPlatforms[i + 13]}
                  className="h-8 w-8 text-[#8fa4c0]"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile featured cards */}
        <div className="space-y-3 pb-16">
          {featuredCards.map((card, i) => (
            <a
              key={card.title}
              href={`/${lang}${card.href}`}
              className="block rounded-[14px] p-6 text-white"
              style={{ background: CARD_GRADIENTS[i] }}
            >
              <p className="mb-2 text-lg font-semibold">{card.title}</p>
              <p className="mb-4 text-sm text-white/80">{card.description}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-white/90">
                {card.cta}
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path
                    d="M5 12H19M12 5L19 12L12 19"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* ── Desktop sticky scroll ──────────────────────────────────────────── */}
      <div
        data-scroll-stage
        className="sticky top-0 hidden h-screen overflow-hidden lg:block"
      >
        {/* Header — fades out as cards rise */}
        <header
          className="absolute inset-x-0 top-0 z-[1] px-4 pt-24 text-center"
          style={{
            opacity: 1 - headerFadeProgress,
            transform: `translateY(${-18 * headerFadeProgress}px)`,
            pointerEvents: headerFadeProgress > 0.5 ? 'none' : 'auto',
          }}
        >
          <h2 className="font-display text-[2.8rem] font-semibold tracking-tight text-slate-800 lg:text-[3.2rem]">
            {t.integration.title}
          </h2>
          <p className="mt-1.5 bg-gradient-to-r from-blue-500 via-sky-400 to-teal-400 bg-clip-text text-[2rem] font-medium tracking-tight text-transparent">
            {t.integration.highlight}
          </p>
          <p className="mx-auto mt-3 max-w-3xl text-base leading-relaxed text-slate-500">
            {t.integration.description}
          </p>
        </header>

        {/* Card grid */}
        <div className="absolute inset-0 z-[2]">
          <div
            className="flex h-full"
            style={{
              // Bleed shrinks during expand: -64px → +48px
              marginLeft: `${-64 + expandProgress * 112}px`,
              marginRight: `${-64 + expandProgress * 112}px`,
              gap: `${COL_GAP + expandProgress * 10}px`,
            }}
          >
            {COLS.map((col, colIdx) => {
              const isCenterGroup = colIdx >= 2 && colIdx <= 4;
              const platforms = colPlatforms[colIdx];

              const colRiseP = clamp((riseProgress - col.riseDelay) / col.riseDuration, 0, 1);

              const colCollapseP = isCenterGroup
                ? 0
                : clamp(
                    (collapseProgress - col.collapseDelay) / Math.max(1 - col.collapseDelay, 0.01),
                    0,
                    1,
                  );

              const colRestY = isCenterGroup ? restY : 0;
              let colTranslateY =
                colRestY +
                (col.startFraction * viewportH - colRestY) * (1 - colRiseP) -
                colCollapseP * (viewportH + 200);

              // During expand, shift center columns up to center the tall cards
              if (isCenterGroup) {
                colTranslateY += expandCenterShift;
              }

              const colOpacity = clamp(colRiseP, 0, 1);

              // During expand: outer cols shrink to 0, center cols fill the space
              // col 3 (middle) grows wider than cols 2 and 4
              const colFlexGrow = !isCenterGroup
                ? Math.max(0, 1 - expandProgress * 5)
                : colIdx === 3
                  ? 1 + expandProgress * 1.5
                  : 1;

              return (
                <div
                  key={colIdx}
                  className="flex min-w-0 flex-col"
                  style={{
                    flex: `${colFlexGrow} 1 0%`,
                    gap: `${COL_GAP}px`,
                    transform: `translateY(${colTranslateY}px)`,
                    opacity: colOpacity,
                  }}
                >
                  {col.slots.map((slotType, cardIdx) => {
                    const platform = platforms[cardIdx] ?? '';
                    const baseH = slotType === 'tall' ? tlH : sqH;

                    const isFeatured = colIdx === 3 && slotType === 'tall';
                    const displayPlatform = isFeatured ? featuredPlatform : platform;
                    const iconIndex = isFeatured
                      ? featuredIconIndex
                      : (colIdx * 3 + cardIdx) % ICON_PATHS.length;

                    // Is this one of the 3 center tall cards that morph?
                    const isCenterTall = isCenterGroup && slotType === 'tall';
                    const featuredCardIdx = isCenterTall ? colIdx - 2 : -1;

                    // Sq cards in center cols detach
                    const sqExtraY =
                      isCenterGroup && slotType === 'sq' ? -sqExitProgress * (viewportH + 200) : 0;

                    // Card height grows during expand for center tall cards
                    const cardH = isCenterTall
                      ? baseH + expandProgress * (EXPAND_CARD_H - baseH)
                      : baseH;

                    return (
                      <div
                        key={`${colIdx}-${cardIdx}`}
                        className="group relative w-full flex-shrink-0 overflow-hidden rounded-2xl"
                        style={{
                          height: `${cardH}px`,
                          ...(sqExtraY !== 0 && { transform: `translateY(${sqExtraY}px)` }),
                          // Increase border radius during expand
                          borderRadius: isCenterTall ? `${16 + expandProgress * 4}px` : undefined,
                        }}
                      >
                        {/* Base background (integration mode) */}
                        <div
                          className="absolute inset-0"
                          style={
                            isFeatured
                              ? {
                                  background:
                                    'radial-gradient(circle at 22% 28%, rgba(104,219,228,0.26), transparent 50%),' +
                                    'linear-gradient(108deg, #4a86d5 0%, #50b0df 52%, #61cfc9 100%)',
                                }
                              : { backgroundColor: '#e3e9f0' }
                          }
                        />

                        {/* Gradient overlay — fades in during expand */}
                        {isCenterTall && (
                          <div
                            className="absolute inset-0"
                            style={{
                              background: CARD_GRADIENTS[featuredCardIdx],
                              opacity: expandProgress,
                            }}
                          />
                        )}

                        {/* Hover gradient (non-featured, pre-expand only) */}
                        {!isFeatured && !isCenterTall && (
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-sky-400 to-teal-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                        )}

                        {/* Integration content — fades out during expand */}
                        <div
                          className="relative z-10 flex h-full flex-col items-center justify-between px-2 py-3"
                          style={isCenterTall ? { opacity: integrationContentOpacity } : undefined}
                        >
                          <span
                            className={`text-[0.52rem] font-semibold uppercase tracking-[0.18em] transition-opacity duration-300 ${
                              isFeatured
                                ? 'text-white/75'
                                : 'opacity-0 text-white/70 group-hover:opacity-100'
                            }`}
                          >
                            {t.integration.hoverPrefix}
                          </span>

                          <PlatformIcon
                            index={iconIndex}
                            platform={displayPlatform}
                            className={`h-10 w-10 transition-colors duration-300 ${
                              isFeatured ? 'text-white' : 'text-[#8fa4c0] group-hover:text-white'
                            }`}
                          />

                          <span
                            className={`text-center text-[0.65rem] font-medium leading-tight transition-opacity duration-300 ${
                              isFeatured
                                ? 'text-white/90'
                                : 'opacity-0 text-white group-hover:opacity-100'
                            }`}
                          >
                            {displayPlatform}
                          </span>
                        </div>

                        {/* Expanded use-case content — fades in during expand */}
                        {isCenterTall && expandProgress > 0 && (
                          <a
                            href={`/${lang}${featuredCards[featuredCardIdx]?.href ?? '/use-cases'}`}
                            className="absolute inset-0 z-20 flex flex-col items-start justify-end p-8 text-white no-underline"
                            style={{
                              opacity: expandedContentOpacity,
                              pointerEvents: expandProgress > 0.5 ? 'auto' : 'none',
                            }}
                          >
                            {/* Platform icon — subtle, top-right */}
                            <div className="absolute right-6 top-6 rounded-xl bg-white/10 p-3">
                              <PlatformIcon
                                index={iconIndex}
                                platform={displayPlatform}
                                className="h-7 w-7 text-white/60"
                              />
                            </div>

                            <h3 className="mb-2 text-2xl font-bold tracking-tight lg:text-3xl">
                              {featuredCards[featuredCardIdx]?.title}
                            </h3>
                            <p className="mb-6 max-w-md text-sm leading-relaxed text-white/75">
                              {featuredCards[featuredCardIdx]?.description}
                            </p>
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-semibold backdrop-blur-sm transition-all duration-200 hover:bg-white/20">
                              {featuredCards[featuredCardIdx]?.cta}
                              <svg
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <path
                                  d="M5 12H19M12 5L19 12L12 19"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
